import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EmailGuard, AuthGuard } from "../App";
import { 
    FaMoneyBillWave, 
    FaUser, 
    FaCheckCircle, 
    FaTimesCircle, 
    FaPen, 
    FaChevronLeft, 
    FaChevronRight, 
    FaDollarSign, 
    FaToggleOn,
    FaHourglassHalf // Icon for PENDING status
} from 'react-icons/fa';
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const AgencyViewingFee = () => {
    const [agencyFees, setAgencyFees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editFee, setEditFee] = useState(null);
    const [feeAmount, setFeeAmount] = useState("");
    const [status, setStatus] = useState("");
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const feesPerPage = 10; // Number of items per page

    useEffect(() => {
        fetchAgencyFees();
    }, []);

    const fetchAgencyFees = async () => {
        const token = localStorage.getItem("token");
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/user/agencyviewingfee/viewagencyviewingfee`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            // Ensure response.data.response is an array, default to empty array
            setAgencyFees(Array.isArray(response.data.response) ? response.data.response : []);
        } catch (error) {
            toast.error("Failed to fetch agency fees");
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (fee) => {
        setEditFee(fee.id);
        // Ensure fee_amount is set as a string for the input field
        setFeeAmount(String(fee.fee_amount)); 
        setStatus(fee.status);
    };

    const handleUpdate = async () => {
        const token = localStorage.getItem("token");
        
        if (!feeAmount || isNaN(feeAmount) || parseFloat(feeAmount) <= 0) {
            toast.error("Fee amount must be a positive number.");
            return;
        }

        try {
            const response = await axios.patch(
                `${StandardURL}/iNyumba/user/agencyviewingfee/updateagencyfee/${editFee}`,
                {
                    // Convert feeAmount back to a float/number for the API
                    fee_amount: parseFloat(feeAmount),
                    status: status,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            toast.success(response.data.response);
            setEditFee(null);
            fetchAgencyFees();
        } catch (error) {
            const errorMessage = error.response?.data?.response || "Failed to update fee.";
            toast.error(errorMessage);
        }
    };

    // --- Pagination Logic ---
    const indexOfLastFee = currentPage * feesPerPage;
    const indexOfFirstFee = indexOfLastFee - feesPerPage;
    const currentFees = agencyFees.slice(indexOfFirstFee, indexOfLastFee);
    const totalPages = Math.ceil(agencyFees.length / feesPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        setEditFee(null); // Close edit form on page change
    }
    
    const nextPage = () => paginate(currentPage + 1);
    const prevPage = () => paginate(currentPage - 1);
    
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }
    
    // UI helper function for status badge (Updated to use PAID/PENDING)
    const getStatusBadge = (status) => {
        const upperStatus = status ? status.toUpperCase() : '';
        if (upperStatus === 'PAID') {
            return (
                <span className="inline-flex items-center px-3 py-1 text-xs font-semibold leading-5 text-green-800 bg-green-100 rounded-full">
                    <FaCheckCircle className="mr-1" /> PAID
                </span>
            );
        }
        if (upperStatus === 'PENDING') {
            return (
                <span className="inline-flex items-center px-3 py-1 text-xs font-semibold leading-5 text-yellow-800 bg-yellow-100 rounded-full">
                    <FaHourglassHalf className="mr-1" /> PENDING
                </span>
            );
        }
        // Fallback
        return (
            <span className="inline-flex items-center px-3 py-1 text-xs font-semibold leading-5 text-gray-800 bg-gray-100 rounded-full">
                {upperStatus}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <Oval color="#4f46e5" secondaryColor="#6366f1" height={50} width={50} ariaLabel="loading-spinner" />
            </div>
        );
    }

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                
                    <div className="max-w-6xl mx-auto">
                        <h1 className="text-3xl font-bold text-gray-900 mb-8 flex items-center justify-center sm:justify-start">
                            <FaMoneyBillWave className="mr-3 text-indigo-600" /> Agency Viewing Fees Management
                        </h1>
                        
                        {/* Table Card */}
                        <div className="bg-white shadow-xl rounded-xl overflow-hidden mb-8">
                            <div className="p-4 bg-indigo-50 border-b border-indigo-100">
                                <h2 className="text-xl font-semibold text-gray-800">Fee Records ({agencyFees.length})</h2>
                            </div>
                            
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                            <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center"><FaUser className="mr-1"/> User ID</th>
                                            <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fee Amount</th>
                                            <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {currentFees.length > 0 ? (
                                            currentFees.map((fee) => (
                                                <tr key={fee.id} className="hover:bg-indigo-50 transition duration-150">
                                                    <td className="py-4 px-6 whitespace-nowrap text-sm font-medium text-gray-900">{fee.id}</td>
                                                    <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-500">{fee.user_id}</td>
                                                    <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-700 font-bold">KES {parseFloat(fee.fee_amount).toFixed(2)}</td>
                                                    <td className="py-4 px-6 whitespace-nowrap text-sm">{getStatusBadge(fee.status)}</td>
                                                    <td className="py-4 px-6 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => handleEdit(fee)}
                                                            className="inline-flex items-center bg-blue-600 text-white py-1.5 px-3 rounded-lg hover:bg-blue-700 transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                        >
                                                            <FaPen className="mr-1.5 text-xs" /> Edit
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="py-8 text-center text-gray-500">No agency fee records found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                    <div className="flex-1 flex justify-between sm:hidden">
                                        <button
                                            onClick={prevPage}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            <FaChevronLeft className="mr-2 h-4 w-4" /> Previous
                                        </button>
                                        <button
                                            onClick={nextPage}
                                            disabled={currentPage === totalPages}
                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Next <FaChevronRight className="ml-2 h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Showing <span className="font-medium">{indexOfFirstFee + 1}</span> to <span className="font-medium">{Math.min(indexOfLastFee, agencyFees.length)}</span> of <span className="font-medium">{agencyFees.length}</span> results
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                <button
                                                    onClick={prevPage}
                                                    disabled={currentPage === 1}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    <FaChevronLeft className="h-5 w-5" aria-hidden="true" />
                                                </button>
                                                {pageNumbers.map(number => (
                                                    <button
                                                        key={number}
                                                        onClick={() => paginate(number)}
                                                        aria-current={number === currentPage ? 'page' : undefined}
                                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                            number === currentPage
                                                                ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {number}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={nextPage}
                                                    disabled={currentPage === totalPages}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    <FaChevronRight className="h-5 w-5" aria-hidden="true" />
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Edit Form Card (Modal-like) */}
                        {editFee && (
                            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                                <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full transform transition-all">
                                    <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-2">
                                        <FaPen className="inline mr-2 text-blue-600"/> Edit Agency Fee (ID: {editFee})
                                    </h2>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <label htmlFor="feeAmount" className="block text-sm font-medium text-gray-700 flex items-center mb-1">
                                                <FaDollarSign className="mr-1"/> Fee Amount (KES)
                                            </label>
                                            <input
                                                type="number"
                                                id="feeAmount"
                                                value={feeAmount}
                                                onChange={(e) => setFeeAmount(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Enter new fee amount"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 flex items-center mb-1">
                                                <FaToggleOn className="mr-1"/> Status
                                            </label>
                                            <select
                                                id="status"
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                                            >
                                                <option value="PAID">PAID</option>
                                                <option value="PENDING">PENDING</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-6 flex justify-end space-x-3">
                                        <button
                                            onClick={() => setEditFee(null)}
                                            className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition duration-150"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleUpdate}
                                            className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition duration-150 shadow-md"
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default AgencyViewingFee;